import {findCategoryById} from '@frontend/helpers/findCategoryById'
import {findLocationById} from '@frontend/helpers/findLocationById'
import {normalizeNumberFromUrl} from '@frontend/helpers/normalizeNumberFromUrl'
import type {
    AdsListRouteParams,
    FilteredItemsPageUrlQueryParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {LocalStorage} from '@frontend/services/LocalStorage'
import {
    useAdsStore,
    useAuthStore,
} from '@frontend/stores'
import {useEffect} from 'react'
import {useParams} from 'react-router-dom'
import {useUrlQueryParams} from 'swayok-react-mdb-ui-kit/helpers/useUrlQueryParams'

// Хук для инициализации страницы с отфильтрованными объявлениями и услугами.
// Возвращает готовность к отображению данных (isReady).
export function useInitFilteredItemsPage(): boolean {
    const loadingUserData = useAuthStore(
        state => state.loadingUserData
    )
    const categories = useAdsStore(
        state => state.categories
    )
    const locations = useAdsStore(
        state => state.locations
    )
    const setSearchPanelKeywords = useAdsStore(
        state => state.setSearchPanelKeywords
    )
    const setSearchPanelLocation = useAdsStore(
        state => state.setSearchPanelLocation
    )
    const setSearchPanelCategory = useAdsStore(
        state => state.setSearchPanelCategory
    )

    // Данные о текущем URL.
    const {
        urlQueryParams,
    } = useUrlQueryParams<FilteredItemsPageUrlQueryParams>()
    const routeParams = useParams<AdsListRouteParams>()

    // Обновление данных в useAdsStore() и LocalStorage из URL query.
    useEffect(() => {
        setSearchPanelKeywords(urlQueryParams.get('keywords') ?? '')
        const locationId = normalizeNumberFromUrl(urlQueryParams.get('location'))
        // Значение locationId может быть 0.
        if (locationId !== null && locationId >= 0) {
            LocalStorage.setFilterLocationId(locationId)
        }
        // При демонтаже сбрасываем категорию.
        return () => {
            setSearchPanelCategory(null)
        }
    }, [])

    // Задать/сменить локацию в панели поиска, если она указана в URL query.
    useEffect(
        () => {
            if (locations.tree.length > 0) {
                const locationId = normalizeNumberFromUrl(urlQueryParams.get('location'))
                // Значение locationId может быть 0.
                if (locationId !== null && locationId >= 0) {
                    const location = findLocationById(locations.tree, locationId)
                    LocalStorage.setFilterLocationId(location?.id ?? 0)
                    setSearchPanelLocation(location ?? null)
                }
            }
        },
        [
            locations.tree.length > 0,
            urlQueryParams.get('location'),
        ]
    )

    // Задать категорию в панели поиска, если она указана в URL params.
    useEffect(() => {
        if (categories.length > 0) {
            const categoryId = normalizeNumberFromUrl(routeParams.id)
            // Значение categoryId НЕ может быть 0.
            if (categoryId && categoryId > 0) {
                setSearchPanelCategory(
                    findCategoryById(categories, categoryId)
                )
            }
        }
    }, [categories.length > 0])

    return !loadingUserData
}
